"use client";

import { isNotUndefined } from "@faire/typescript";
import { cn } from "@faire/web/tools/cn";
import { useUniqueId } from "@faire/web/ui/hooks/useUniqueId";
import { parseUnit } from "@faire/web/ui/lib";
import React from "react";

import Color from "../../Color";

import {
  ICON_SIZES,
  ICON_SIZE_SIZES_PX_UNITLESS,
  ICON_SIZE_STROKE_WIDTHS_PX_UNITLESS,
} from "./consts";
import { isPx, isRem, pxOrRemToUnitlessPx } from "./sizeUnitUtils";
import { SVGIconPropsV2 } from "./types";
import { isIconSize } from "./utils";

// NOTE: We can't use tailwind for icon yet because icons are used in several projects where tailwind hasn't been configured
export const SVGIconV2: React.FC<
  SVGIconPropsV2 & React.RefAttributes<SVGElement>
> = ({
  children,
  className,
  titleAccess,
  viewBox,
  ref,
  margin,
  size: sizeProp,
  width,
  height,
  fontSize,
  strokeWidth,
  color,
  fill,
  style,
  component,
  ...rest
}) => {
  const labelId = useUniqueId("titleAccess-");

  const Component = component ?? "svg";
  const vars: Record<string, string> = {};
  const size = sizeProp ?? fontSize ?? width ?? height;

  if (color) {
    // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
    vars["--f-svg-color"] = Color[color] ?? color;
  }
  if (fill) {
    vars["--f-svg-fill"] = fill;
  }
  if (size) {
    vars["--f-svg-font-size"] = parseUnit(
      isIconSize(size) ? ICON_SIZE_SIZES_PX_UNITLESS[size] : size
    );
  }
  if (margin) {
    vars["--f-svg-margin"] = margin;
  }

  return (
    <Component
      ref={ref}
      className={cn("svgicon", className, {
        "svgicon-font-size": size !== undefined,
      })}
      focusable="false"
      viewBox={viewBox ?? "0 0 24 24"}
      aria-labelledby={titleAccess ? labelId : undefined}
      aria-hidden={titleAccess ? undefined : "true"}
      role={titleAccess ? "img" : "presentation"}
      strokeWidth={resolveStrokeWidth({
        size,
        strokeWidth,
      })}
      style={{
        ...vars,
        ...style,
      }}
      {...rest}
    >
      {children}
      {titleAccess ? <title id={labelId}>{titleAccess}</title> : null}
    </Component>
  );
};

const resolveStrokeWidth = ({
  size,
  strokeWidth,
}: {
  size: string | number | undefined;
  strokeWidth: string | number | undefined;
}) => {
  if (isIconSize(size)) {
    return strokeWidthLinearRegressionFn(ICON_SIZE_SIZES_PX_UNITLESS[size]);
  }
  if (typeof size === "number") {
    return strokeWidthLinearRegressionFn(size);
  }
  if (typeof size === "string" && (isRem(size) || isPx(size))) {
    return strokeWidthLinearRegressionFn(pxOrRemToUnitlessPx(size));
  }
  if (isNotUndefined(strokeWidth)) {
    return strokeWidth;
  }
  return undefined;
};

/**
 * Given a list of [x,y] values, returns a function which predicts the associated y-value for a given x-value via linear regression, with the following caveats:
 *  - if the x-value is out of bounds of the provided [x,y] values, then the min or max [x,y] is used for the y-value
 *  - if the x-value exactly matches one of the provided [x,y] values, then that [x,y] is used for the y-value
 */
const getClampedLinearRegressionFn = (
  coords: [number, number][]
): ((x: number) => number) => {
  // Handle edge cases
  if (!coords.length) {
    return (_x: number) => 0;
  }

  if (coords.length === 1) {
    // With only one point, return that y value for any x
    return (_x: number) => coords[0]![1];
  }

  // Calculate sums needed for linear regression
  const n = coords.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  let minXCoord = coords[0]!;
  let maxXCoord = coords.at(-1)!;

  for (const [x, y] of coords) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
    if (x < minXCoord[0]) {
      minXCoord = [x, y];
    }
    if (x > maxXCoord[0]) {
      maxXCoord = [x, y];
    }
  }

  // Calculate slope (m) and y-intercept (b) using least squares method
  // m = (n * Σ(xy) - Σx * Σy) / (n * Σ(x²) - (Σx)²)
  const denominator = n * sumX2 - sumX * sumX;

  // Handle the case where all x values are the same (vertical line)
  if (denominator === 0) {
    // Return the average y value for any x
    const avgY = sumY / n;
    return (_x: number) => avgY;
  }

  const m = (n * sumXY - sumX * sumY) / denominator;

  // b = (Σy - m * Σx) / n
  const b = (sumY - m * sumX) / n;

  // Return a function which takes an x-value and returns predicted y-value
  return (x: number) => {
    // we want to clamp the linear regression to the end coords, if x is out of the bounds then use the closest bound as the y-value
    if (x < minXCoord[0]) {
      return minXCoord[1];
    }
    if (x > maxXCoord[0]) {
      return maxXCoord[1];
    }
    // if x is an exact match in original coords, directly use the corresponding coord y-value, otherwise predict the y-value
    const matchingCoord = coords.find(([coordX]) => coordX === x);
    return matchingCoord?.[1] ?? m * x + b;
  };
};

const strokeWidthLinearRegressionFn = getClampedLinearRegressionFn(
  ICON_SIZES.map((size) => [
    ICON_SIZE_SIZES_PX_UNITLESS[size],
    ICON_SIZE_STROKE_WIDTHS_PX_UNITLESS[size],
  ])
);
